/* global isMasterAdmin, previews, folderNameError, canvas_data */
import * as instacart from "@frontend/constants/instacart-constants";
import * as walmart from "@frontend/constants/walmart-constants";
import * as bynder from "@frontend/constants/bynder";
import * as optimizely from "@frontend/constants/optimizely";
import { GeneratePreviewImage } from "@frontend/group/generate-preview-image";
import store from '@frontend/store';
import savedDownloadSettingsAPI from '@frontend/services/api/layout-saved-download-settings';
import { changeDpiBlob } from "changedpi";
import {
    FIELD_TYPE_TEXT,
    FIELD_TYPE_MULTI_LINE_TEXT,
} from '@frontend/constants/type-fields-of-template';
import {
    BYNDER_EVENTS,
    bynderEventBroker,
} from '@frontend/group/modules/bynder/event-broker';

const MAX_NAME_LENGHT = 40;

let optimizelySavedSettingsCache = [];
let bynderSavedSettingsCache = [];
let allTemplatesCache = [];
let groupsTemplatesCache = [];
let currentPagePreviewsCache = [];

export const getImageFromInstance = async (
    instanceId,
    previews,
    dpi = null,
    type = 'jpeg',
    quality = 0.8
) => {
    const instance = previews.find(preview => preview.id === instanceId);
    const templateName = sanitizeTemplateName(instance);
    const fileName = `${templateName}_image.${type}`;

    const tempCanvas = document.createElement('canvas');
    const ctx = tempCanvas.getContext('2d');

    tempCanvas.width = instance.canvas.width;
    tempCanvas.height = instance.canvas.height;

    if (type === 'jpeg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    }

    instance.canvas.discardActiveObject();

    ctx.drawImage(instance.canvas.getElement(), 0, 0, tempCanvas.width, tempCanvas.height);
    const dataUrl = tempCanvas.toDataURL(`image/${type}`, quality);

    return await convertDataUrlToFile(dataUrl, fileName, dpi, type);
}

export const convertUrlToFile = async (
    url,
    fileName,
    type = 'application/pdf'
) => {
    const response = await axios.get(url, { responseType: 'blob' });

    return new File([response.data], fileName, { type });
}

export const convertDataUrlToFile = async (
    dataUrl,
    fileName = 'image.jpeg',
    dpi = null,
    type = 'jpeg'
) => {
    const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    let blob = new Blob([byteArray], { type: `image/${type}` });

    if (dpi) {
        blob = await changeDpiBlob(blob, dpi);
    }

    const file = new File([blob], fileName, { type: `image/${type}` });

    return file;
}

export const clearProducts = () => {
    $('#instacart-products').empty();
    $('#additional-product-ids-table').empty();
    $('#single-add-property').empty();
    $('#walmart-products').empty();
    $('#walmart-bulk-products').empty();
    $('#walmart-bulk-products-flex').empty();
    $('#additional-property-select').empty();
}

export const clearResults = () => {
    $('#publish_to_platform_result').empty();
    $('.asset-error').empty();
    $('#publishing_image_spinner').addClass('d-none');
    $('#salsify-publish-result').addClass('d-none');
    $('#salsify-publish-result-content').empty();
    $('#publish-progress-container').addClass('d-none');
    $('#publish-progress-text').html('');
    clearWalmartResponse();
}

export const resetButtons = () => {
    $('#publishToPlatform').removeAttr('disabled');
    $('#cancelPublish').text('Close');
    $('#publishToPlatform').html('Publish');
    $('#cancelPublish').removeClass('btn-primary').addClass('btn-secondary');
}

export const clearInstacartInputs = () => {
    $('#campaign-name').val('');
    $('#campaign-budget').val('1');
    $('#ad-group-name').val('');
    $('#default-bid').val('15');
    $('#ad-group-tagline').val('');
    $('.asset-alt-text').each(function () {
        $(this).val($(this).data('origin-value'));
    });
    $('.instacart-product-id').prop('checked', false);
    $('#existing-campaign').empty();
    $('#existing-ad-group').empty();
    $('#new-to-instacart').prop('checked', false);
    $('#targeting-everyone').prop('checked', false);

    clearInstacartAssets();
}

export const clearInstacartAssets = () => {
    $('.instacart-asset').each(function () {
        $(this).val('');
        $(this).removeAttr('data-alt-text');
        $(this).removeAttr('data-token');
        $(this).removeAttr('data-name');
        $(this).removeAttr('data-asset-type');
    });
}

export const instacartErrors = {
    campaignNameError: $('#campaign-name-error'),
    campaignBudgetError: $('#campaign-budget-error'),
    adGroupNameError: $('#ad-group-name-error'),
    defaultBidError: $('#default-bid-error'),
    adGroupTaglineError: $('#ad-group-tagline-error'),
    existingCampaignsError: $('#existing-campaigns-error'),
    campaignError: $('#instacart-campaign-error'),
    adGroupError: $('#instacart-ad-group-error')
}

export const clearInstacartErrors = () => {
    Object.values(instacartErrors).forEach(element => element.html(''));
}

export const getInstance = (previews, instanceId) => {
    return previews.find(preview => preview.id === instanceId);
}

export const fillWalmartInputs = (previews, instanceId) => {
    const headlineInput = $('#walmart-headline');
    const subheadInput = $('#walmart-subhead');
    const ctaInput = $('#walmart-cta');
    const imageAltTextInput = $('#walmart-image-alt-text');
    const logoAltTextInput = $('#walmart-logo-alt-text');

    const instance = getInstance(previews, instanceId);

    const headline = instance?.data?.multi_line_text_headline_1;
    const subhead = instance?.data?.multi_line_text_subhead_1;
    const cta = instance?.data?.multi_line_text_cta;

    if (headline) {
        headlineInput.val(headline);
        imageAltTextInput.val(headline);
        logoAltTextInput.val(headline);
    }

    if (subhead) {
        subheadInput.val(subhead);
    }

    if (cta) {
        ctaInput.val(cta);
    }

    clearWalmartFormErrors()
    validateWalmartForm()
}

export const fetchImages = (imageData = []) => {
    if (imageData.length) {
        const imageNames = imageData.map(url => {
            const decodedUrl = decodeURIComponent(url);

            const imageName = decodedUrl.split('/').pop().split('&')[0];

            return { [imageName]: decodedUrl };
        });

        return imageNames;
    } else {
        return [];
    }
}

export const clearWalmartInputs = async () => {
    const settings = await getPublishPreferences(walmart.CREATIVE_SETTINGS);

    if (settings) {
        $('#walmart-creative-name').val(settings.creative_name);
        $('#walmart-headline').val(settings.headline).trigger('input');
        $('#walmart-subhead').val(settings.subhead).trigger('input');
        $('#walmart-image-alt-text').val(settings.image_alt_text).trigger('input');
        $('#walmart-logo-alt-text').val(settings.logo_alt_text).trigger('input');
        $('#walmart-cta').val(settings.cta || 'Shop now').trigger('input');

        // $('#walmart-headline-30').val(settings?.headline_30 ?? '').trigger('input'); // Reserved for future ad units
        // $('#walmart-headline-35').val(settings?.headline_35 ?? '').trigger('input'); // Reserved for future ad units
        $('#walmart-subhead-35').val(settings?.subhead_35 ?? '').trigger('input');
        $('#walmart-subhead-40').val(settings?.subhead_40 ?? '').trigger('input');
        $('#walmart-subhead-55').val(settings?.subhead_55 ?? '').trigger('input');

        validateWalmartForm();
    } else {
        $('#walmart-creative-name').val('');
        $('#walmart-headline').val('');
        $('#walmart-subhead').val('');
        $('#walmart-image-alt-text').val('');
        $('#walmart-logo-alt-text').val('');
        $('#walmart-cta').val('Shop now');
    }
}

export const walmartErrors = {
    creativeNameError: $('#walmart-creative-name-error'),
    headlineError: $('#walmart-headline-error'),
    subheadError: $('#walmart-subhead-error'),
    // headline30Error: $('#walmart-headline-30-error'), // Reserved for future ad units
    // headline35Error: $('#walmart-headline-35-error'), // Reserved for future ad units
    subhead35Error: $('#walmart-subhead-35-error'),
    subhead40Error: $('#walmart-subhead-40-error'),
    subhead55Error: $('#walmart-subhead-55-error'),
    ctaError: $('#walmart-cta-error'),
    imageAltTextError: $('#walmart-image-alt-text-error'),
    logoAltTextError: $('#walmart-logo-alt-text-error')
}

export const clearWalmartFormErrors = () => {
    Object.values(walmartErrors).forEach(element => element.html('').siblings('input').removeClass('is-invalid'));
}

export const validateWalmartForm = () => {
    let isError = false;
    const creativeName = $('#walmart-creative-name').val();
    const headline = $('#walmart-headline').val();
    const subhead = $('#walmart-subhead').val();
    // const headline30 = $('#walmart-headline-30').val(); // Reserved for future ad units
    // const headline35 = $('#walmart-headline-35').val(); // Reserved for future ad units
    const subhead35 = $('#walmart-subhead-35').val();
    const subhead40 = $('#walmart-subhead-40').val();
    const subhead55 = $('#walmart-subhead-55').val();
    const imageAltText = $('#walmart-image-alt-text').val();
    const logoAltText = $('#walmart-logo-alt-text').val();
    const cta = $('#walmart-cta').val();
    const companyId = $('#company-id').val();

    if (!companyId && isMasterAdmin) {
        alert('Select Company');
        isError = true;
    }

    if (!creativeName) {
        walmartErrors.creativeNameError.html('Creative name is required').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (!headline) {
        walmartErrors.headlineError.html('Headline is required').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (headline.length > 25) {
        walmartErrors.headlineError.html('Headline should be max 25 characters').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (!subhead) {
        walmartErrors.subheadError.html('Subhead is required').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (subhead.length > 30) {
        walmartErrors.subheadError.html('Subhead should be max 30 characters').siblings('input').addClass('is-invalid');
        isError = true;
    }

    const subheadRegex = /[.!?*]$/;

    if (!subheadRegex.test(subhead)) {
        walmartErrors.subheadError.html('Subhead should end with one of these characters . ! ? *').siblings('input').addClass('is-invalid');
        isError = true;
    }

    // // Validate headline_30 - Reserved for future ad units
    // if (headline30 && headline30.length > 30) {
    //     walmartErrors.headline30Error.html('Headline should be max 30 characters').siblings('input').addClass('is-invalid');
    //     isError = true;
    // }

    // // Validate headline_35 - Reserved for future ad units
    // if (headline35 && headline35.length > 35) {
    //     walmartErrors.headline35Error.html('Headline should be max 35 characters').siblings('input').addClass('is-invalid');
    //     isError = true;
    // }

    // Validate subhead_35
    if (subhead35 && subhead35.length > 35) {
        walmartErrors.subhead35Error.html('Subhead should be max 35 characters').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (subhead35 && !subheadRegex.test(subhead35)) {
        walmartErrors.subhead35Error.html('Subhead should end with one of these characters . ! ? *').siblings('input').addClass('is-invalid');
        isError = true;
    }

    // Validate subhead_40
    if (subhead40 && subhead40.length > 40) {
        walmartErrors.subhead40Error.html('Subhead should be max 40 characters').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (subhead40 && !subheadRegex.test(subhead40)) {
        walmartErrors.subhead40Error.html('Subhead should end with one of these characters . ! ? *').siblings('input').addClass('is-invalid');
        isError = true;
    }

    // Validate subhead_55
    if (subhead55 && subhead55.length > 55) {
        walmartErrors.subhead55Error.html('Subhead should be max 55 characters').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (subhead55 && !subheadRegex.test(subhead55)) {
        walmartErrors.subhead55Error.html('Subhead should end with one of these characters . ! ? *').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (!imageAltText) {
        walmartErrors.imageAltTextError.html('Image alt text is required').siblings('input').addClass('is-invalid');
        isError = true;
    }

    const numericRegex = /\d/;
    const specialCharsAltTextRegex = /[.,!/]$/;

    if (numericRegex.test(imageAltText)) {
        walmartErrors.imageAltTextError.html('Image alt text must not include numbers.<br> Instead, write numbers as words.').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (specialCharsAltTextRegex.test(imageAltText)) {
        walmartErrors.imageAltTextError.html('Image alt text must not end with special characters . , ! /').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (!logoAltText) {
        walmartErrors.logoAltTextError.html('Logo alt text is required').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (numericRegex.test(logoAltText)) {
        walmartErrors.logoAltTextError.html('Logo alt text must not include numbers.<br> Instead, write numbers as words.').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (specialCharsAltTextRegex.test(logoAltText)) {
        walmartErrors.logoAltTextError.html('Logo alt text must not end with special characters . , ! /').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (!cta) {
        walmartErrors.ctaError.html('Cta is required').siblings('input').addClass('is-invalid');
        isError = true;
    }

    if (cta.length > 16) {
        walmartErrors.ctaError.html('Cta should be max 16 characters').siblings('input').addClass('is-invalid');
        isError = true;
    }

    const ctaRegex = /^[A-Z][a-z]*( [a-z][a-z]*)*[.!?]?$/;

    if (!ctaRegex.test(cta)) {
        walmartErrors.ctaError
            .html('Only the first word should be capitalized and CTA can end with one of these characters . ! ?')
            .siblings('input')
            .addClass('is-invalid');
        isError = true;
    }

    $('#walmart-create-creative').prop('disabled', isError)

    return !isError;
}

export const btnLoading = (button, loadingText = 'Loading...') => {
    $(button).prop('disabled', true).html(
        `<span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>${loadingText}`
    );
};

export const btnLoaded = (button, defaultText = 'Submit', disabled = false) => {
    $(button).prop('disabled', disabled).html(defaultText);
};

export const fetchCampaigns = async (formData) => {
    const { data } = await axios.post('/instacart/fetch-campaigns', formData);

    return data;
}

export const fetchCampaign = async (formData) => {
    const { data } = await axios.post('/instacart/fetch-campaign', formData);

    return data;
}

export const fillExistingCampaignInputs = (data) => {
    $('#instacart-new-campaign').removeClass('d-none');

    $('#campaign-name').val(data.data.attributes.name);
    $('#campaign-type').val(data.data.attributes.goal_format);
    $('#pacing-type').val(data.data.attributes.pacing_type);
    $('#campaign-budget').val(data.data.attributes.budget);
    $('#campaign-starts-at').val(data.data.attributes.scheduled_start_at_date);
    $('#campaign-ends-at').val(data.data.attributes.scheduled_end_at_date);

    $('#instacart-campaign-id').val(data.data.id);
}

export const fetchAdGroups = async (formData) => {
    const { data } = await axios.post('/instacart/fetch-ad-groups', formData);

    return data;
}

export const fetchAdGroup = async (formData) => {
    const { data } = await axios.post('/instacart/fetch-ad-group', formData);

    return data;
}

export const fillExistingAdGroupInputs = (data) => {
    $('#instacart-new-ad-group').removeClass('d-none');

    $('#ad-group-name').val(data.data.attributes.name ?? '');
    $('#default-bid').val(data.data.attributes.default_bid ?? 15);
    $('#ad-group-tagline').val(data.data.attributes.creative.properties.tagline ?? '');
    $('#new-to-instacart').prop('checked', data.data.attributes.targeting_strategy.user_targeting.new_to_instacart ?? false);
    $('#targeting-everyone').prop('checked', data.data.attributes.targeting_strategy.user_targeting.targeting_everyone ?? false);

    $('#instacart-ad-group-id').val(data.data.id ?? '');
    $('#instacart-creative-id').val(data.data.attributes.creative.id ?? '');

    Object.keys(data.data.attributes.creative.assets).forEach(key => {
        const asset = data.data.attributes.creative.assets[key];

        if (key === instacart.ASSET_TYPES.TOP_IMAGE_WEB) {
            $('#top-image-web').val(asset.id);
            $('#top-image-web').attr('data-alt-text', asset.alt_text);
            $('#top-image-web').attr('data-token', asset.token);
            $('#top-image-web').attr('data-name', asset.uploaded_file_name);
            $('#top-image-web').attr('data-asset-type', key);
        }

        if (key === instacart.ASSET_TYPES.TOP_IMAGE_MOBILE) {
            $('#top-image-mobile').val(asset.id);
            $('#top-image-mobile').attr('data-alt-text', asset.alt_text);
            $('#top-image-mobile').attr('data-token', asset.token);
            $('#top-image-mobile').attr('data-name', asset.uploaded_file_name);
            $('#top-image-mobile').attr('data-asset-type', key);
        }

        if (key === instacart.ASSET_TYPES.HERO_IMAGE) {
            $('#hero-image').val(asset.id);
            $('#hero-image').attr('data-alt-text', asset.alt_text);
            $('#hero-image').attr('data-token', asset.token);
            $('#hero-image').attr('data-name', asset.uploaded_file_name);
            $('#hero-image').attr('data-asset-type', key);
        }

        if (key === instacart.ASSET_TYPES.BRAND_LOGO) {
            $('#brand-logo').val(asset.id);
            $('#brand-logo').attr('data-alt-text', asset.alt_text);
            $('#brand-logo').attr('data-token', asset.token);
            $('#brand-logo').attr('data-name', asset.uploaded_file_name);
            $('#brand-logo').attr('data-asset-type', key);
        }
    })
}

export const getAssetIdForUpdate = (assetType) => {
    switch (assetType) {
        case instacart.ASSET_TYPES.TOP_IMAGE_WEB:
            return $('#top-image-web').val();
        case instacart.ASSET_TYPES.TOP_IMAGE_MOBILE:
            return $('#top-image-mobile').val();
        case instacart.ASSET_TYPES.HERO_IMAGE:
            return $('#hero-image').val();
        case instacart.ASSET_TYPES.BRAND_LOGO:
            return $('#brand-logo').val();
        default:
            return null;
    }
}

export const isInstacartAvailable = async (platformId) => {
    const { data } = await axios.post('/instacart/is-platform-available', { platform_id: platformId });

    if (data === true) {
        $('#instacart-fields').removeClass('d-none');
        $('#instacart-not-available').addClass('d-none');
    } else {
        $('#instacart-fields').addClass('d-none');
        $('#instacart-not-available').removeClass('d-none');
    }
}

export const updateCampaign = async () => {
    toastr.clear();
    clearInstacartErrors();

    let isError = false;

    const platformId = $('#deployment_platform').find(':selected').val();
    const companyId = $('#company-id').val();

    const campaignType = $('#campaign-type').find(':selected').val();
    const pacingType = $('#pacing-type').find(':selected').val();
    const campaignName = $('#campaign-name').val();
    const campaignBudget = $('#campaign-budget').val();
    const campaignStartsAt = $('#campaign-starts-at').val();
    const campaignEndsAt = $('#campaign-ends-at').val();

    const campaignId = $('#instacart-campaign-id').val();

    if (!companyId && isMasterAdmin) {
        alert('Select Company');
        return;
    }

    if (!campaignName) {
        instacartErrors.campaignNameError.html('Campaign name is required');
        isError = true;
    }

    if (!campaignBudget) {
        instacartErrors.campaignBudgetError.html('Campaign budget is required');
        isError = true;
    }

    if (campaignBudget < 1) {
        instacartErrors.campaignBudgetError.html('Campaign budget must be a minimum of 1');
        isError = true;
    }

    if (isError) {
        toastr.error('Please check the errors');
        return;
    }

    const formData = {
        platform_id: platformId,
        company_id: companyId,
        campaign_type: campaignType,
        pacing_type: pacingType,
        campaign_name: campaignName,
        campaign_budget: campaignBudget,
        campaign_starts_at: campaignStartsAt,
        campaign_ends_at: campaignEndsAt,
        campaign_id: campaignId
    };

    btnLoading('#update-campaign-btn', 'Updating...');

    const { data } = await axios.post('/instacart/update-campaign', formData);

    if (data.status === 'error' || data.status === 'no_code' || data.status === 'invalid_grant') {
        instacartErrors.campaignError.html(data.message);
        btnLoaded('#update-campaign-btn', 'Update Campaign');
        return;
    }

    toastr.success('Campaign updated successfully');
    btnLoaded('#update-campaign-btn', 'Update Campaign');

    return;
}

export const updateAdGroup = async () => {
    toastr.clear();
    clearInstacartErrors();

    let isError = false;

    const platformId = $('#deployment_platform').find(':selected').val();
    const companyId = $('#company-id').val();
    const campaignType = $('#campaign-type').find(':selected').val();

    const adGroupName = $('#ad-group-name').val();
    const defaultBid = $('#default-bid').val();
    const adGroupTagline = $('#ad-group-tagline').val();
    const newToInstacart = $('#new-to-instacart').is(':checked');
    const targetingEveryone = $('#targeting-everyone').is(':checked');

    const campaignId = $('#instacart-campaign-id').val();
    const adGroupId = $('#instacart-ad-group-id').val();
    const creativeId = $('#instacart-creative-id').val();

    if (!companyId && isMasterAdmin) {
        alert('Select Company');
        return;
    }

    if (!adGroupName) {
        instacartErrors.adGroupNameError.html('Ad group name is required');
        isError = true;
    }

    if (!defaultBid) {
        instacartErrors.defaultBidError.html('Default bid is required');
        isError = true;
    }

    if (defaultBid < 15) {
        instacartErrors.defaultBidError.html('Default bid must be a minimum of 15');
        $('#default-bid').val(15);
        isError = true;
    }

    if (!adGroupTagline) {
        instacartErrors.adGroupTaglineError.html('Ad group tagline is required');
        isError = true;
    }

    if (isError) {
        toastr.error('Please check the errors');
        return;
    }

    let filesData = [];

    $('.instacart-asset').each(function () {
        if ($(this).val()) {
            filesData.push({
                file_name: $(this).data('name'),
                token: $(this).data('token'),
                asset_type: $(this).data('asset-type'),
                alt_text: $(this).data('alt-text'),
                asset_id: $(this).val()
            });
        }
    });

    const formData = {
        platform_id: platformId,
        company_id: companyId,
        campaign_id: campaignId,
        campaign_type: campaignType,
        ad_group_name: adGroupName,
        default_bid: defaultBid,
        ad_group_tagline: adGroupTagline,
        new_to_instacart: newToInstacart,
        targeting_everyone: targetingEveryone,
        ad_group_id: adGroupId,
        creative_id: creativeId,
        files_data: JSON.stringify(filesData)
    }

    btnLoading('#update-ad-group-btn', 'Updating...');

    const { data } = await axios.post('/instacart/update-ad-group', formData);

    if (data.status === 'error' || data.status === 'no_code' || data.status === 'invalid_grant') {
        instacartErrors.adGroupError.html(data.message);
        btnLoaded('#update-ad-group-btn', 'Update Ad Group');
        return;
    }

    toastr.success('Ad Group updated successfully');
    btnLoaded('#update-ad-group-btn', 'Update Ad Group');

    return;
}

export const clearWalmartResponse = () => {
    $('#walmart-publish-response').html('');
    $('#walmart-publish-error').html('');
    $('.walmart-image-error').html('');
    $('#walmart-validation-errors-container').addClass('d-none');
    $('#walmart-validation-errors-content').removeClass('show').addClass('collapse');
    $('#walmart-validation-errors-chevron').removeClass('fa-chevron-up').addClass('fa-chevron-down');
    $('#walmart-validation-errors').html('');
}

export const clearWalmartAssetResponse = () => {
    $('.image-response').addClass('d-none');
    $('.image-response-text').html('');
}

export const validateRequiredCreativeTypes = (selectedTypes) => {
    toastr.clear();

    const missingTypes = Object.keys(walmart.requiredUnitTypes).filter(type => !selectedTypes.includes(type));

    if (missingTypes.length > 0) {
        toastr.options.timeOut = 0;
        toastr.error(
            [
                'Make sure all asset types are visible on the page - set # of templates per page to 25.',
                'Missing:',
                ...missingTypes.map(type => walmart.requiredUnitTypes[type])
            ].join('<br>')
        ).css('width', 'auto');

        showMissingWalmartTemplatesModal(missingTypes);

        return false;
    }

    if (!selectedTypes.includes(walmart.adUnits.TILE_APP)) {
        toastr.warning(`${walmart.optionalUnitTypes[walmart.adUnits.TILE_APP]} is missing - Tile Desktop image will be used for Tile App.`);
    }

    return true;
}

const showMissingWalmartTemplatesModal = async (missingTypes) => {
    const missingList = missingTypes
        .map(type => `<li>${walmart.requiredUnitTypes[type]}</li>`)
        .join('');

    const { isConfirmed } = await window.Swal.fire({
        title: 'Missing Walmart templates',
        html: `
            <ul class="text-left">${missingList}</ul>
            <p class="small text-muted">If a template is already in the layout, just select it or make it visible on the page.</p>
            <p>Create the missing templates in this layout?</p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Create templates',
        cancelButtonText: 'Cancel',
    });

    if (!isConfirmed) {
        return;
    }

    const layoutId = $('#template-group-preview').attr('data-layout-id');

    try {
        const { data } = await axios.post('/walmart/create-missing-templates', {
            layout_id: layoutId,
            ad_units: missingTypes,
        });

        if (data?.status !== 'success') {
            toastr.error(data?.message || 'Failed to create templates');
            return;
        }

        const added = [...Object.values(data.data.created), ...Object.values(data.data.reused)];

        if (!added.length) {
            toastr.info('Templates already exist in the layout - select them / set # of templates per page to 15.');
            return;
        }

        toastr.success(`Templates added to the layout: ${added.join(', ')}`);
        location.reload();
    } catch (error) {
        toastr.error(error?.response?.data?.message || 'Failed to create templates');
    }
}

const formatWalmartErrorMessage = (message) => {
    try {
        const parsedMessage = JSON.parse(message);
        if (Array.isArray(parsedMessage) && parsedMessage.length > 0) {
            const errorObj = parsedMessage[0];
            if (errorObj.message && typeof errorObj.message === 'string') {
                const validationErrors = errorObj.message.split('\n').filter(line => line.trim() && line.includes(':'));
                const errorCount = validationErrors.length;

                if (errorCount > 0) {
                    let formattedHtml = `<strong>Errors (${errorCount} found):</strong><br><ul style="margin: 10px 0; padding-left: 20px;">`;

                    validationErrors.forEach(error => {
                        const cleanError = error.trim().replace(/^\$\.adUnits\./, '');
                        formattedHtml += `<li style="margin: 5px 0;">${cleanError}</li>`;
                    });

                    formattedHtml += '</ul>';
                    return formattedHtml;
                }
            }
        }
    } catch (e) {
        console.warn('Error parsing Walmart error message:', e);
    }

    return message;
}

export const createCreative = async () => {
    const walmartResponse = $('#walmart-publish-response');
    const walmartError = $('#walmart-publish-error');

    const validationErrorsContainer = $('#walmart-validation-errors-container');
    const validationErrors = $('#walmart-validation-errors');

    clearWalmartResponse();
    clearWalmartFormErrors();

    const progressBarContainer = $('#publish-progress-container');
    const progressBar = $('#publish-progress-bar');

    const assets = store.getters['walmart/getAssets'];
    const checkDefaultAssets = store.getters['walmart/checkGalleryDesktopAssets'];

    let activeInstances = [];
    let filesData = [];
    let assetsData = [];

    const uploadType = $('input[name="walmart-upload-type"]:checked').val();

    let singleUpload = false;
    let walmartBulkPublish = false;
    let assetSelector = false;
    let bulkPublishFlex = false;

    if (uploadType === 'walmart-single-upload') {
        singleUpload = true;
    }
    if (uploadType === 'walmart-bulk-upload') {
        walmartBulkPublish = true;
    }
    if (uploadType === 'walmart-asset-selector') {
        assetSelector = true;
    }
    if (uploadType === 'walmart-bulk-upload-flex') {
        bulkPublishFlex = true;
    }

    const customerId = $('#template-group-preview').attr('data-customer-id');
    const layoutId = $('#template-group-preview').attr('data-layout-id');
    const layoutLink = window.location.href;

    const creativeName = $('#walmart-creative-name').val();
    const headline = $('#walmart-headline').val();
    const subhead = $('#walmart-subhead').val();
    const imageAltText = $('#walmart-image-alt-text').val();
    const logoAltText = $('#walmart-logo-alt-text').val();
    const cta = $('#walmart-cta').val();
    const platformId = $('#deployment_platform').find(':selected').val();
    const companyId = $('#company-id').val();
    const folderId = $('#walmart-folder-id').val();

    // const headline30 = $('#walmart-headline-30').val(); // Reserved for future ad units
    // const headline35 = $('#walmart-headline-35').val(); // Reserved for future ad units
    const subhead35 = $('#walmart-subhead-35').val();
    const subhead40 = $('#walmart-subhead-40').val();
    const subhead55 = $('#walmart-subhead-55').val();

    if (!validateWalmartForm()) {
        toastr.error('Please check the errors');
        return;
    }

    const creativeSettings = {
        creative_name: creativeName,
        headline: headline,
        subhead: subhead,
        image_alt_text: imageAltText,
        logo_alt_text: logoAltText,
        cta: cta,
        // headline_30: headline30, // Reserved for future ad units
        // headline_35: headline35, // Reserved for future ad units
        subhead_35: subhead35,
        subhead_40: subhead40,
        subhead_55: subhead55
    };

    await storePublishPreferences(walmart.CREATIVE_SETTINGS, JSON.stringify(creativeSettings));

    const formData = {
        'creative_name': creativeName,
        'headline': headline,
        'subhead': subhead,
        'image_alt_text': imageAltText,
        'logo_alt_text': logoAltText,
        'cta': cta,
        'platform_id': platformId,
        'company_id': companyId,
        'folder_id': folderId,
        'is_bulk_publish': walmartBulkPublish,
        'layout_id': layoutId,
        'layout_link': layoutLink,
        'customer_id': customerId,
        'is_asset_selector': assetSelector,
        'is_single_upload': singleUpload,
        'is_bulk_publish_flex': bulkPublishFlex,
        // 'headline_30': headline30, // Reserved for future ad units
        // 'headline_35': headline35, // Reserved for future ad units
        'subhead_35': subhead35,
        'subhead_40': subhead40,
        'subhead_55': subhead55
    };

    const fileData = new FormData();
    fileData.set('platform_id', platformId);
    fileData.set('company_id', companyId);

    if (singleUpload) {
        const logoId = $('#walmart-logo-image-id').val();
        const lifestyleId = $('#walmart-lifestyle-image-id').val();

        if (!logoId || !lifestyleId) {
            alert('Please upload Logo and Lifestyle');
            return;
        }

        formData.logo_id = logoId;
        formData.lifestyle_id = lifestyleId;

        btnLoading('#walmart-create-creative', 'Creating...');
    }

    if (walmartBulkPublish) {
        const selectedTypes = [];

        $('.walmart-creative-image-bulk:checked').each(function () {
            const instanceId = $(this).val();
            const creativeType = $('.walmart-creative-image-type[data-instance-id="' + instanceId + '"]').val();

            if (creativeType) {
                selectedTypes.push(creativeType);
            }
        });

        if (!validateRequiredCreativeTypes(selectedTypes)) {
            return;
        }

        $('.walmart-creative-image-bulk').each(function () {
            let instanceId = null;
            if ($(this).is(':checked')) {
                instanceId = $(this).val();
                activeInstances.push(instanceId);
            }
        });

        const totalInstances = activeInstances.length;
        let processedInstances = 0;

        progressBarContainer.removeClass('d-none');
        progressBar.css('width', '0%').attr('aria-valuenow', 0);

        for (const el of activeInstances) {
            const imageResponse = $('.image-response[data-instance-id="' + el + '"]');
            const imageResponseText = $('.image-response-text[data-instance-id="' + el + '"]');
            const imageError = $('.walmart-image-error[data-instance-id="' + el + '"]');

            const creativeType = $('.walmart-creative-image-type[data-instance-id="' + el + '"]').val();

            const file = await getImageFromInstance(el, previews, 300, 'jpeg', 1);

            fileData.set('file', file);
            fileData.set('is_bulk_publish', walmartBulkPublish);

            btnLoading('#walmart-create-creative', 'Creating...');

            const { data } = await axios.post('/walmart/upload-creative-image', fileData);

            if (data?.result?.original?.status === 'error') {
                imageError.html(JSON.stringify(data?.result?.original?.message));
                btnLoaded('#walmart-create-creative', 'Create the Creative');
                return;
            }

            if (data?.status === 'error') {
                imageError.html(data.message);
                btnLoaded('#walmart-create-creative', 'Create the Creative');
                return;
            }

            imageResponse.removeClass('d-none');
            imageResponseText.html(data.data[0].assetId);

            filesData.push({
                asset_id: data.data[0].assetId,
                creative_type: creativeType
            });

            processedInstances++;
            const progressPercent = Math.round((processedInstances / totalInstances) * 100);
            progressBar.css('width', `${progressPercent}%`).attr('aria-valuenow', progressPercent).html(`${progressPercent.toFixed(0)}%`);
        }
    }

    if (bulkPublishFlex) {
        const selectedTypes = [];

        const $selectedOptions = $('.walmart-creative-image-flex').find(':selected');

        $selectedOptions.each(function () {
            const select = $(this).closest('.walmart-creative-image-flex');
            const instanceId = select.data('instance-id');

            const creativeType = $('.walmart-creative-image-type-flex[data-instance-id="' + instanceId + '"]').val();

            if (creativeType) {
                selectedTypes.push(creativeType);
            }
        });

        if (!validateRequiredCreativeTypes(selectedTypes)) {
            return;
        }

        const totalOptions = $selectedOptions.length;
        let processedOptions = 0;

        progressBarContainer.removeClass('d-none');
        progressBar.css('width', '0%').attr('aria-valuenow', 0);

        for (const option of $selectedOptions) {
            const $option = $(option);

            const select = $option.closest('.walmart-creative-image-flex');
            const instanceId = select.data('instance-id');

            const imageResponse = $('.image-response[data-instance-id="' + instanceId + '"]');
            const imageResponseText = $('.image-response-text[data-instance-id="' + instanceId + '"]');
            const imageError = $('.walmart-image-error[data-instance-id="' + instanceId + '"]');

            const creativeType = $('.walmart-creative-image-type-flex[data-instance-id="' + instanceId + '"]').val();

            const isCanvasImage = $option.data('canvas-image');

            fileData.set('is_canvas_image', isCanvasImage);

            if (isCanvasImage) {
                fileData.set('file', await convertDataUrlToFile($option.val(), $option.data('file-name')));
            } else {
                fileData.set('image_url', $option.val());
            }

            btnLoading('#walmart-create-creative', 'Creating...');

            const { data } = await axios.post('/walmart/upload-creative-image', fileData);

            if (data?.result?.original?.status === 'error') {
                imageError.html(JSON.stringify(data?.result?.original?.message));
                btnLoaded('#walmart-create-creative', 'Create the Creative');
                return;
            }

            if (data?.status === 'error') {
                imageError.html(data.message);
                btnLoaded('#walmart-create-creative', 'Create the Creative');
                return;
            }

            imageResponse.removeClass('d-none');
            imageResponseText.html(data.data[0].assetId);

            filesData.push({
                asset_id: data.data[0].assetId,
                creative_type: creativeType
            });

            processedOptions++;
            const progressPercent = Math.round((processedOptions / totalOptions) * 100);
            progressBar.css('width', `${progressPercent}%`).attr('aria-valuenow', progressPercent).html(`${progressPercent.toFixed(0)}%`);
        }
    }

    if (assetSelector) {
        if (assets.length < 2 || !checkDefaultAssets) {
            toastr.options = {
                timeOut: 10000,
                extendedTimeOut: 0
            };

            toastr.error('You need to select at least Image and Logo for Gallery Desktop.');
            return;
        }

        assetsData = assets;

        btnLoading('#walmart-create-creative', 'Creating...');
    }

    formData.files_data = JSON.stringify(filesData);
    formData.assets_data = JSON.stringify(assetsData);
    formData.use_logo_per_template = $('#walmart-logo-mode-bulk').is(':checked');

    try {
        const { data } = await axios.post('/walmart/create-the-creatives', formData);

        if (data?.result?.original?.status === 'error') {
            walmartError.html(JSON.stringify(data?.result?.original?.message));
            return;
        }

        if (data?.status === 'error' && data?.code === 400) {
            validationErrorsContainer.removeClass('d-none');
            validationErrors.html(formatWalmartErrorMessage(data.message));
            return;
        }

        if (data?.status === 'error') {
            walmartError.html(data.message);
            return;
        }

        const heading = $('<p>', {
            text: 'You can check your Creative Previews here'
        });

        const button = $('<a>', {
            text: 'Previews',
            class: 'btn btn-primary btn-sm',
            href: '/walmart/show-preview?creative_id=' + data.data[0].creativeId + '&platform_id=' + platformId + '&company_id=' + companyId,
            target: '_blank'
        });

        const divWrapper = $('<div>', {
            class: 'form-group text-center preview-wrapper mt-2'
        });

        divWrapper.append(heading);
        divWrapper.append(button);

        walmartResponse.append(divWrapper);

        btnLoaded('#walmart-create-creative', 'Create the Creative');
    } catch (e) {
        console.log(e);
    } finally {
        btnLoaded('#walmart-create-creative', 'Create the Creative');
    }
}

export const newWalmartFolder = () => {
    $('#walmart-folder-name').removeClass('d-none');
    $('#walmart-create-folder').removeClass('d-none');
    $('#walmart-cancel-folder').removeClass('d-none');
    $('#walmart-delete-folder').addClass('d-none');
    $('#walmart-new-folder').addClass('d-none');
}

export const cancelWalmartFolder = () => {
    $('#walmart-folder-name').addClass('d-none').val('');
    $('#walmart-create-folder').addClass('d-none');
    $('#walmart-cancel-folder').addClass('d-none');
    $('#walmart-delete-folder').removeClass('d-none');
    $('#walmart-new-folder').removeClass('d-none');
}

export const createWalmartFolder = async () => {
    toastr.clear();
    toastr.options = {
        timeOut: 10000,
        extendedTimeOut: 0
    };

    const folderNameError = $('#walmart-folder-name-error');

    $('#walmart-folders-error').html('');
    folderNameError.html('');

    const folderName = $('#walmart-folder-name').val();
    const platformId = $('#deployment_platform').find(':selected').val();
    const parentFolder = $('#walmart-folder-id').val();

    if (!parentFolder) {
        toastr.error('Select Folder');
        return;
    }

    const companyId = $('#company-id').val();

    if (!companyId && isMasterAdmin) {
        toastr.error('Select Company');
        return;
    }

    if (!folderName) {
        folderNameError.html('Folder name is required');
        return;
    }

    const formData = {
        'folder_name': folderName,
        'platform_id': platformId,
        'parent_folder': parentFolder,
        'company_id': companyId
    };

    btnLoading('#walmart-create-folder', 'Creating...');

    const { data } = await axios.post('/walmart/create-folder', formData);

    try {
        if (data?.result?.original?.status === 'error') {
            folderNameError.html(extractErrorMessage(data.result.original));
            return;
        }

        if (data?.status === 'error') {
            folderNameError.html(extractErrorMessage(data));
            return;
        }

        const newFolder = {
            id: data.data[0].folderId,
            name: folderName,
            is_default: false
        };

        cancelWalmartFolder();

        fetchWalmartFolders(newFolder);

        toastr.success('Folder Created');
    } catch (e) {
        console.log('Folder error', e);
    } finally {
        btnLoaded('#walmart-create-folder', '<i class="bi bi-check2"></i> Create Folder');
    }

}

export const deleteWalmartFolder = async () => {
    toastr.clear();
    toastr.options = {
        timeOut: 10000,
        extendedTimeOut: 0
    };

    const companyId = $('#company-id').val();

    if (!companyId && isMasterAdmin) {
        alert('Select Company');
        return;
    }

    const formData = {
        'platform_id': $('#deployment_platform').find(':selected').val(),
        'company_id': companyId,
        'folder_id': $('#walmart-folder-id').val()
    };

    btnLoading('#walmart-delete-folder', 'Deleting...');

    try {
        const { data } = await axios.post('/walmart/delete-folder', formData);

        if (data?.result?.original?.status === 'error') {
            toastr.error(JSON.stringify(data?.result?.original?.message));
            return;
        }

        if (data?.status === 'error') {
            const errorMessage = extractErrorMessage(data);

            toastr.error(errorMessage);
            return;
        }

        toastr.success('Folder deleted successfully');
        fetchWalmartFolders();
    } catch (error) {
        toastr.error('Something went wrong while deleting the folder');
    } finally {
        btnLoaded('#walmart-delete-folder', '<i class="bi bi-trash3"></i> Delete Folder');
    }
}

export const formatImageOption = (option) => {
    if (!option.id) return option.text;

    const imgUrl = $(option.element).data('img');

    return $(`
      <span class="d-flex align-items-center">
        <img src="${imgUrl}" style="height: 30px; margin-right: 8px;" />
        <span style="margin-left: auto;">${option.text}</span>
      </span>
    `);
}

export const formatImageSelection = (option) => {
    if (!option.id) return option.text;

    const imgUrl = $(option.element).data('img');

    return $(`
      <span class="d-flex align-items-center">
        <img src="${imgUrl}" style="height: 15px; margin-right: 4px;" />
        <span style="margin-left: auto;">${option.text}</span>
      </span>
    `);
}

export const walmartImageSelect = (id, selectClass) => {
    const select = $('.' + selectClass + '[data-instance-id="' + id + '"]');

    select.select2({
        placeholder: 'Select image',
        allowClear: true,
        templateResult: formatImageOption,
        templateSelection: formatImageSelection,
        dropdownParent: $('#publishToPlatformModal'),
    });

    select.next('.select2-container').find('.select2-search__field').css({ 'font-size': '80%' });
    select.next('.select2-container').find('.select2-selection--single').css({ 'height': '32px' });

    return select;
}

export const walmartGetImages = (instanceId, previews, selectClass = 'walmart-creative-image') => {
    const imageOptions = {
        format: 'jpeg',
        quality: 0.8
    };

    const select = walmartImageSelect(instanceId, selectClass);

    select.empty();

    const instance = getInstance(previews, instanceId);
    const templateName = sanitizeTemplateName(instance);

    const imageData = instance.data.background ?? [];

    const images = fetchImages(imageData);

    select.removeClass('d-none');

    const groups = instance.canvas.getObjects('group');

    let entireImage = instance.canvas.toDataURL(imageOptions);
    let entireName = `${templateName}_entire.jpeg`;
    let entireNameTruncated = truncateImgName(entireName, 30);

    select.append(
        `<option
            value="${entireImage}"
            data-img="${entireImage}"
            data-canvas-image="true"
            data-file-name="${entireName}"
        >
            ${entireNameTruncated}
        </option>`
    );

    if (groups.length) {
        const groupOptions = groups
            .map((group, index) => {
                let image = group.toDataURL(imageOptions);

                let groupName = `${templateName}_group_${index + 1}.jpeg`;
                let groupNameTruncated = truncateImgName(groupName, 30);

                return `
                    <option
                        value="${image}"
                        data-img="${image}"
                        data-canvas-image="true"
                        data-file-name="${groupName}"
                    >
                        ${groupNameTruncated}
                    </option>
                `;
            })
            .join('');

        select.append(groupOptions);
    }

    if (images.length) {
        const options = images
            .filter(item => {
                const [key, value] = Object.entries(item)[0];
                return key !== 'null' && value !== 'null' && value !== null;
            })
            .map(item => {
                const [key, value] = Object.entries(item)[0];

                let bgImgNameTruncated = truncateImgName(key, 30);

                return value
                    ? `<option
                      value="${value}"
                      data-img="${value}"
                      data-canvas-image="false"
                      data-file-name="${key}"
                  >
                      ${bgImgNameTruncated}
                  </option>`
                    : '';
            })
            .join('');

        select.append(options);
    }

    const additionalBgImages = instance.canvas.getObjects('image').filter(image =>
        image.element_id.includes('add_bk_theme_img') ||
        image.element_id.includes('additional')
    );

    if (additionalBgImages.length) {
        const additionalBgImagesOptions = additionalBgImages
            .map(image => {
                let additionalBgImgName = `${image?.name ? sanitizeName(image.name) : image.element_id}.jpeg`;
                let additionalBgImgNameTruncated = truncateImgName(additionalBgImgName, 30);

                let imageUrl = image.toDataURL(imageOptions);

                return `
                    <option
                        value="${imageUrl}"
                        data-img="${imageUrl}"
                        data-canvas-image="true"
                        data-file-name="${additionalBgImgName}"
                    >
                        ${additionalBgImgNameTruncated}
                    </option>
                `;
            })
            .join('');

        select.append(additionalBgImagesOptions);
    }

    const productImages = instance.canvas.getObjects('image').filter(image =>
        image.element_id.includes('product_image')
    );

    if (productImages.length) {
        const productImagesOptions = productImages.map(image => {
            let productImgName = `${image?.name ? sanitizeName(image.name) : image.element_id}.png`;
            let productImgNameTruncated = truncateImgName(productImgName, 30);

            let productImgUrl = image.toDataURL({
                format: 'png',
                quality: 0.8
            });

            return `
                <option
                    value="${productImgUrl}"
                    data-img="${productImgUrl}"
                    data-canvas-image="true"
                    data-file-name="${productImgName}"
                >
                    ${productImgNameTruncated}
                </option>
            `;
        })
            .join('');

        select.append(productImagesOptions);
    }
}

export const handleWalmartUpload = async (instanceId, walmartImageData) => {
    const layoutId = $("#template-group-preview").attr("data-layout-id");
    const platformId = $('#deployment_platform').find(':selected').val();
    const imageResponse = $('.image-response[data-instance-id="' + instanceId + '"]');
    const imageError = $('.walmart-image-error[data-instance-id="' + instanceId + '"]');

    imageError.html('');
    imageResponse.empty();

    const companyId = $('#company-id').val();

    if (!companyId && isMasterAdmin) {
        alert('Select Company');
        return;
    }

    const formData = new FormData();

    formData.append('layout_id', layoutId);
    formData.append('instance_id', instanceId);
    formData.append('platform_id', platformId);
    formData.append('company_id', companyId);

    btnLoading('.walmart-upload-btn[data-instance-id="' + instanceId + '"]', 'Publishing...');

    for (let index = 0; index < walmartImageData[instanceId].length; index++) {
        const item = walmartImageData[instanceId][index];
        let isCanvasImage = item.isCanvasImage;
        let fileName = item.fileName;

        formData.set('is_canvas_image', isCanvasImage);

        if (isCanvasImage) {
            formData.set('file', await convertDataUrlToFile(item.value, fileName));
        } else {
            formData.set('image_url', item.value);
        }

        try {
            const { data } = await axios.post('/walmart/upload-creative-image', formData);

            if (data?.result?.original?.status === 'error') {
                imageError.html(JSON.stringify(data?.result?.original?.message));
                btnLoaded('.walmart-upload-btn[data-instance-id="' + instanceId + '"]', 'Publish');
                return;
            }

            if (data?.status === 'error') {
                imageError.html(data.message);
                btnLoaded('.walmart-upload-btn[data-instance-id="' + instanceId + '"]', 'Publish');
                return;
            }

            imageResponse.removeClass('d-none');

            let assetId = data.data[0].assetId;

            let itemName = truncateImgName(item.fileName);

            const responseBlock = `
                <div class="d-flex align-items-center mb-2">
                    <div class="d-flex align-items-center w-100">
                        <p
                            class="mb-0 mr-2 mt-2 flex-grow-1"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="Asset ID: ${assetId}"
                        >
                            Uploaded:
                            <span class="text-success image-response-text mr-2" data-asset-id="${assetId}">
                                ${itemName}
                            </span>
                        </p>
                        <button
                            class="btn btn-sm btn-outline-primary copy-asset-id-btn mr-2"
                            data-asset-id="${assetId}"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="Copy Asset ID"
                        >
                            <i class="bi bi-clipboard" aria-hidden="true"></i>
                        </button>
                    </div>
                    <div class="dropdown ml-auto" id="walmart-use-buttons-${assetId}">
                        <button
                            data-asset-id="${assetId}"
                            type="button"
                            class="btn btn-sm btn-primary dropdown-toggle walmart-use-buttons mr-2"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                            style="width: 145px;"
                        >
                            Select image type
                        </button>
                        <div class="dropdown-menu">
                            <button
                                data-asset-id="${assetId}"
                                type="button"
                                class="dropdown-item walmart-use-as-lifestyle"
                                style="width: 145px;"
                            >
                                Lifestyle
                            </button>
                            <button
                                data-asset-id="${assetId}"
                                type="button"
                                class="dropdown-item walmart-use-as-logo"
                                style="width: 145px;"
                            >
                                Logo
                            </button>
                        </div>
                    </div>
                    <div>
                        <button
                            data-asset-id="${assetId}"
                            type="button"
                            class="btn btn-sm btn-outline-secondary drop-asset-selection-btn"
                            disabled
                            data-toggle="tooltip"
                            data-placement="top"
                            title="Drop selection"
                        >
                            <i class="bi bi-x-circle"></i>
                        </button>
                    </div>
                </div>
                `;

            imageResponse.append(responseBlock);

            $(function () {
                $('[data-toggle="tooltip"]').tooltip();
            });
        } catch (error) {
            imageError.html('An error occurred while uploading.');
            console.error(error);
        }
    }

    btnLoaded('.walmart-upload-btn[data-instance-id="' + instanceId + '"]', 'Publish');
}

export const fetchWalmartFolders = async (newFolder = null) => {
    $('#walmart-folder-name-error').html('');

    const foldersError = $('#walmart-folders-error');
    foldersError.html('');

    const platformId = Number($('#deployment_platform').find(':selected').val());

    let companyId = $('#company-id').val();
    companyId = companyId ? Number(companyId) : null;

    if (!companyId && isMasterAdmin) {
        alert('Select Company');
        return;
    }

    const formData = {
        'platform_id': platformId,
        'company_id': companyId,
        'is_default_folder': true
    };

    btnLoading('#request-walmart-folders', 'Loading...');

    try {
        const { data } = await axios.post('/walmart/fetch-folders', formData);

        if (data?.status === 'error') {
            const errorMessage = extractErrorMessage(data);

            foldersError.html(errorMessage);
            return;
        }

        if (data?.data?.status === 'error') {
            foldersError.html(data.data.message);
            return;
        }

        if (data?.result?.original?.status === 'error') {
            folderNameError.html(extractErrorMessage(data.result.original));
            return;
        }

        const defaultFolder = {
            id: data.folder_id,
            name: data.folder_name,
            is_default: true
        };

        const nestedFolders = [];

        data.data.response.forEach(folder => {
            nestedFolders.push({
                id: folder.folderId,
                name: folder.name,
                is_default: false
            });
        });

        $('#walmart-folders').children('walmart-folder-tree').remove();

        const el = document.createElement('walmart-folder-tree');

        el.defaultFolder = defaultFolder;
        el.nestedFolders = nestedFolders;
        el.platformId = platformId;
        el.companyId = companyId ?? null;
        el.newFolder = newFolder;

        $('#walmart-folders').prepend(el);

    } catch (error) {
        foldersError.html('Error fetching folders. Please try again.');
    } finally {
        btnLoaded('#request-walmart-folders', '<i class="bi bi-arrow-clockwise"></i> Request Folders');
    }
}

export const extractErrorMessage = (data) => {
    if (data?.status !== 'error') return null;

    try {
        const parsedMessage = JSON.parse(data.message);

        if (Array.isArray(parsedMessage) && parsedMessage[0]?.details) {
            return parsedMessage[0].details.join(", ");
        }
        else if (parsedMessage?.details) {
            return parsedMessage.details.join(", ");
        }

        return data.message;
    } catch (e) {
        return data.message;
    }
}

export const sanitizeTemplateName = (instance) => {
    return instance.template.name.replace(/\s+/g, ' ').replace(/[^\w\s]/gi, '').replace(/\s/g, '_');
}

export const clearBynderProducts = () => {
    $('#bynder-single-product').html('');
    $('#bynder-multi-products').empty();
    $('#bynder-grouped-products').empty().addClass('d-none');
    $('.additional-metaproperties').addClass('d-none');
}

export const clearBynderResponse = () => {
    $('.bynder-image-response').html('');
    $('.bynder-image-error').html('');
}

export const clearOptimizelyProducts = () => {
    $('#optimizely-single-product').html('');
    $('#optimizely-multi-products').empty();
}

export const getFileGroups = (allTemplates, groupsTemplates, groupLayersNameByProjectName) => {
    const templatesByInstanceId = new Map(allTemplates.map((template) => [String(template.instance_id), template]));

    const toFileGroup = (instanceIds) => ({
        instanceIds,
        templateNames: instanceIds
            .map((id) => templatesByInstanceId.get(id)?.name)
            .filter(Boolean),
    });

    if (groupLayersNameByProjectName) {
        const byProjectName = new Map();
        const ungrouped = [];

        allTemplates.forEach((template) => {
            const instanceId = String(template.instance_id);
            const projectName = template.project_name;

            if (!projectName) {
                ungrouped.push([instanceId]);
                return;
            }

            if (!byProjectName.has(projectName)) {
                byProjectName.set(projectName, []);
            }

            byProjectName.get(projectName).push(instanceId);
        });

        return [...byProjectName.values(), ...ungrouped].map(toFileGroup);
    }

    const groupedInstanceIds = new Set();

    const originalGroups = (groupsTemplates || [])
        .map((group) => (group.instancesId || [])
            .map(String)
            .filter((instanceId) => templatesByInstanceId.has(instanceId)))
        .filter((instanceIds) => instanceIds.length > 0);

    originalGroups.forEach((instanceIds) => instanceIds.forEach((id) => groupedInstanceIds.add(id)));

    allTemplates.forEach((template) => {
        const instanceId = String(template.instance_id);

        if (!groupedInstanceIds.has(instanceId)) {
            originalGroups.push([instanceId]);
        }
    });

    return originalGroups.map(toFileGroup);
}

const renderOptimizelyMultiProductRows = () => {
    $('#optimizely-multi-products').empty();

    if (!allTemplatesCache.length) {
        return;
    }

    const currentPagePreviewsById = new Map(currentPagePreviewsCache.map((preview) => [preview.id, preview]));
    const selectedSetting = getSelectedOptimizelySavedSettings();

    const isPdfExport = selectedSetting?.settings?.exportFormat === 'pdf';
    const groupLayersNameByProjectName = isPdfExport && (selectedSetting?.settings?.groupLayersNameByProjectName ?? false);

    const fileGroups = getFileGroups(
        allTemplatesCache,
        isPdfExport ? groupsTemplatesCache : [],
        groupLayersNameByProjectName
    );

    let counter = 1;

    for (const fileGroup of fileGroups) {
        const groupId = fileGroup.instanceIds[0];
        const matchingPreview = fileGroup.instanceIds
            .map((instanceId) => currentPagePreviewsById.get(instanceId))
            .find(Boolean);
        const thumbnail = matchingPreview
            ? new GeneratePreviewImage(matchingPreview).resize(200, 100).url()
            : null;
        const label = fileGroup.templateNames.join(', ');

        $('#optimizely-multi-products').append(`
          <div class="form-group border rounded optimizely-multi-product-container p-3 mb-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <label class="form-check m-0 d-flex align-items-center mr-3">
                <input class="form-check-input optimizely-multi-product optimizely-instance-id mb-2" type="checkbox" value="${groupId}" data-instance-ids='${JSON.stringify(fileGroup.instanceIds)}' data-template-name="${label}" />
                <span class="input-label">
                  #${counter} ${label}
                </span>
              </label>
              ${thumbnail ? `
              <div class="preview-thumbnail">
                <img src="${thumbnail}" alt="preview-thumbnail">
              </div>` : ''}
            </div>

            <div class="optimizely-meta-fields d-none" data-instance-id="${groupId}">
              <meta-fields></meta-fields>
            </div>

            <div class="mt-2">
              <span class="text-success optimizely-image-response" data-instance-id="${groupId}"></span>
              <span class="text-danger optimizely-image-error" data-instance-id="${groupId}"></span>
            </div>
          </div>
        `);

        counter++;
    }
}

export const renderOptimizelyMultiProducts = async (layoutId, currentPagePreviews, groupsTemplates = []) => {
    const { data: allTemplates } = await axios.get(`/banner/groups/${layoutId}/templates`, {
        params: { simplified: true, sortBySettingsOrder: true, page: 1, limit: 999 },
    });

    allTemplatesCache = allTemplates;
    groupsTemplatesCache = groupsTemplates || [];
    currentPagePreviewsCache = currentPagePreviews;

    renderOptimizelyMultiProductRows();
}

export const regroupOptimizelyMultiProducts = () => {
    renderOptimizelyMultiProductRows();
}

export const getOptimizelyTemplateByInstanceId = (instanceId) => {
    return allTemplatesCache.find((template) => String(template.instance_id) === String(instanceId));
}

export const loadOptimizelySavedSettings = async () => {
    try {
        const { data } = await savedDownloadSettingsAPI.index({ export_formats: ['pdf', 'pptx'] });
        optimizelySavedSettingsCache = data.settings;

        const $select = $('#optimizely-saved-settings');
        $select.empty();

        optimizelySavedSettingsCache.forEach((setting) => {
            $select.append(`<option value="${setting.id}">${setting.name}</option>`);
        });

        $select.trigger('change');
    } catch (error) {
        console.error('Error loading saved settings for Optimizely', error);
    }
}

export const getSelectedOptimizelySavedSettings = () => {
    const selectedId = $('#optimizely-saved-settings').find(':selected').val();

    return optimizelySavedSettingsCache.find((setting) => String(setting.id) === String(selectedId));
}

export const loadBynderSavedSettings = async () => {
    try {
        const { data } = await savedDownloadSettingsAPI.index({ export_formats: ['pdf', 'pptx'] });
        bynderSavedSettingsCache = data.settings;

        const $select = $('#bynder-saved-settings');
        $select.empty();

        bynderSavedSettingsCache.forEach((setting) => {
            $select.append(`<option value="${setting.id}">${setting.name}</option>`);
        });

        $select.trigger('change');
    } catch (error) {
        console.error('Error loading saved settings for Bynder', error);
    }
}

export const getSelectedBynderSavedSettings = () => {
    const selectedId = $('#bynder-saved-settings').find(':selected').val();

    return bynderSavedSettingsCache.find((setting) => String(setting.id) === String(selectedId));
}

export const renderBynderGroupedProducts = () => {
    const $container = $('#bynder-grouped-products');
    $container.empty();

    if (!allTemplatesCache.length) {
        return;
    }

    const currentPagePreviewsById = new Map(currentPagePreviewsCache.map((preview) => [preview.id, preview]));
    const selectedSetting = getSelectedBynderSavedSettings();

    const isPdfExport = selectedSetting?.settings?.exportFormat === 'pdf';
    const groupLayersNameByProjectName = isPdfExport && (selectedSetting?.settings?.groupLayersNameByProjectName ?? false);

    const fileGroups = getFileGroups(
        allTemplatesCache,
        isPdfExport ? groupsTemplatesCache : [],
        groupLayersNameByProjectName
    );

    let counter = 1;

    for (const fileGroup of fileGroups) {
        const groupId = fileGroup.instanceIds[0];

        const matchingPreview = fileGroup.instanceIds
            .map((instanceId) => currentPagePreviewsById.get(instanceId))
            .find(Boolean);

        const thumbnail = matchingPreview
            ? new GeneratePreviewImage(matchingPreview).resize(200, 100).url()
            : null;

        const label = fileGroup.templateNames.join(', ');

        $container.append(`
          <div class="form-group border rounded bynder-multi-product-container p-3 mb-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <label class="form-check m-0 d-flex align-items-center mr-3">
                <input class="form-check-input bynder-multi-product bynder-instance-id mb-2" type="checkbox" value="${groupId}" data-instance-ids='${JSON.stringify(fileGroup.instanceIds)}' data-template-name="${label}" />
                <span class="input-label">
                  #${counter} ${label}
                </span>
              </label>
              ${thumbnail ? `
              <div class="preview-thumbnail">
                <img src="${thumbnail}" alt="preview-thumbnail">
              </div>` : ''}
            </div>

            <div class="additional-metaproperties d-none" data-instance-id="${groupId}">
              <additional-metaproperties></additional-metaproperties>
            </div>

            <div class="mt-2">
              <span class="text-success bynder-image-response" data-instance-id="${groupId}"></span>
              <span class="text-danger bynder-image-error" data-instance-id="${groupId}"></span>
            </div>
          </div>
        `);

        counter++;
    }

    if (!$('#bynder-configure-metadata').hasClass('d-none')) {
        appendAdditionalMetaproprties();
    }
}

const isSingleFile = (settings) => {
    if (!settings) {
        return false;
    }

    const groupLayersNameByProjectName = settings.exportFormat === 'pdf' && (settings.groupLayersNameByProjectName ?? false);
    const createOneFilePerGroup = settings.createOneFilePerGroup ?? false;

    return (settings.downloadAsOneFile ?? false) && !groupLayersNameByProjectName && !createOneFilePerGroup;
}

export const toggleOptimizelyMetaFields = () => {
    const selectedSetting = getSelectedOptimizelySavedSettings();
    const isOneFile = isSingleFile(selectedSetting?.settings);

    $('#optimizely-bulk-meta-fields').toggleClass('d-none', !isOneFile);
    $('.optimizely-meta-fields[data-instance-id]').toggleClass('d-none', isOneFile);
}

export const clearOptimizelyResponse = () => {
    $('.optimizely-image-response').html('');
    $('.optimizely-image-error').html('');
}

export const buildFileForInstances = async (instanceIds, templateName, imageType, dpi, generateExportFile) => {
    if (imageType === 'pdf') {
        const groupInstancesMap = instanceIds.map((id) => ({ instancesId: [id] }));
        const groupIndices = instanceIds.map((_, index) => index);

        const exportResult = await generateExportFile(
            'pdf', instanceIds, groupIndices, null, null, dpi, false, true, true,
            null, null, null, null, false, true, true, null, null, null, null, null,
            null, false, groupInstancesMap, false, false,
            'Product_ID', false, true, false
        );

        return convertUrlToFile(exportResult.url, `${templateName}.pdf`);
    }

    if (imageType === 'pptx') {
        const isGroup = instanceIds.length > 1;

        const exportResult = await generateExportFile(
            'pptx', instanceIds, null, null, null, dpi, false, true, true,
            null, null, null, null, false, isGroup, false
        );

        return convertUrlToFile(
            exportResult.url,
            `${templateName}.pptx`,
            'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        );
    }

    return null;
};

const getSelectedBynderInstanceGroups = () => {
    const groups = [];

    const singleProductId = $('#bynder-single-product-id').val();

    if (singleProductId) {
        groups.push([singleProductId]);
    }

    $('#bynder-grouped-products .bynder-multi-product:checked').each(function () {
        const instanceIds = JSON.parse($(this).attr('data-instance-ids') || 'null') || [$(this).val()];
        groups.push(instanceIds);
    });

    return groups;
}

export const publishBynderWithSettings = async (isBynderMeta = false, bynderMeta = null) => {
    clearBynderResponse();

    toastr.clear();

    let isPublishSuccess = false;

    try {
        const selectedSavedSetting = getSelectedBynderSavedSettings();

        if (!selectedSavedSetting) {
            toastr.error('Select saved settings');
            return;
        }

        const settings = selectedSavedSetting.settings;

        const layoutId = $('#template-group-preview').attr('data-layout-id');
        const customerId = $('#template-group-preview').attr('data-customer-id');
        const layoutLink = window.location.href;
        const platformId = $('#deployment_platform').find(':selected').val();

        const selectedGroups = getSelectedBynderInstanceGroups();

        if (selectedGroups.length === 0) {
            toastr.options = {
                timeOut: 10000,
                extendedTimeOut: 0
            };

            toastr.error('Select at least one image');

            return;
        }

        const isOneFile = isSingleFile(settings);

        const publishGroups = isOneFile
            ? [selectedGroups.flat()]
            : selectedGroups;

        const items = publishGroups.map((instanceGroup) => {
            const instanceId = instanceGroup[0];
            const templateName = $(`#bynder-grouped-products .bynder-multi-product[value="${instanceId}"]`).data('template-name')
                || previews.find((preview) => preview.id === instanceId)?.template?.name
                || '';

            const propertiesInstanceId = isOneFile ? bynder.BYNDER_ONE_FILE_INSTANCE_ID : instanceId;

            return {
                instance_ids: instanceGroup,
                template_name: sanitizeTemplateName({ template: { name: String(templateName) } }),
                additional_properties: isBynderMeta
                    ? store.getters['bynder/additionalPropertiesById'](propertiesInstanceId)
                    : null,
            };
        });

        btnLoading('#bynder-publish', 'Publishing...');

        const { data } = await axios.post('/bynder/file-upload', {
            platform_id: platformId,
            layout_id: layoutId,
            customer_id: customerId,
            layout_link: layoutLink,
            settings,
            items,
            is_bynder_meta: isBynderMeta,
            bynder_meta: bynderMeta,
        });

        if (data.status === 'queued') {
            toastr.success(data.message);
            isPublishSuccess = true;
        } else if (data.status === 'error') {
            toastr.error(data.message);
        } else {
            let successCount = 0;
            let errorCount = 0;

            (data.results || []).forEach((result) => {
                const target = result.status === 'error'
                    ? $(`.bynder-image-error[data-instance-id="${result.instance_id}"]`)
                    : $(`.bynder-image-response[data-instance-id="${result.instance_id}"]`);

                target.html(result.message);

                if (result.status === 'error') {
                    errorCount++;
                } else {
                    successCount++;
                }
            });

            if (errorCount === 0) {
                toastr.success('Successfully published to Bynder');
                isPublishSuccess = true;
            } else if (successCount === 0) {
                toastr.error('Failed to publish to Bynder');
            } else {
                toastr.warning(`Published ${successCount} of ${successCount + errorCount} items — see details below`);
                isPublishSuccess = true;
            }
        }
    } catch (error) {
        console.error('Failed to publish to Bynder:', error);
    } finally {
        if (isPublishSuccess) {
            $('#cancelPublish').removeClass('btn-secondary').addClass('btn-primary');
        }

        btnLoaded('#bynder-publish', 'Publish to Bynder', isPublishSuccess);
    }
}

export const publishToBynder = async (isBynderMeta = false, bynderMeta = null, generateExportFile = null) => {
    clearBynderResponse();

    toastr.clear();

    const progressBarContainer = $('#publish-progress-container');
    const progressBar = $('#publish-progress-bar');

    let isPublishSuccess = false;

    try {
        const uploadType = $('#bynder-upload-type').val();
        const dpi = $('#bynder-dpi').find(':selected').val();
        const imageType = $('#bynder-image-type').find(':selected').val();

        if (imageType === bynder.BYNDER_IMAGE_TYPES.PDF || imageType === bynder.BYNDER_IMAGE_TYPES.PPTX) {
            await publishBynderWithSettings(isBynderMeta, bynderMeta);
            return;
        }

        const layoutId = $("#template-group-preview").attr("data-layout-id");
        const customerId = $("#template-group-preview").attr("data-customer-id");

        let includePsd = false;

        const layoutLink = window.location.href;
        const platformId = $('#deployment_platform').find(':selected').val();
        const publishInBackground = $('#bynder-publish-in-background').is(':checked');
        const publishAsOneFile = $('#bynder-publish-as-one-file').is(':checked');

        let additionalProperties = null;

        const formData = new FormData();

        formData.append('platform_id', platformId);
        formData.append('layout_link', layoutLink);
        formData.append('image_type', imageType);
        formData.append('layout_id', layoutId);
        formData.append('dpi', dpi);

        if (isBynderMeta) {
            formData.append('is_bynder_meta', true);
            formData.append('bynder_meta', JSON.stringify(bynderMeta));
        }

        let instancesToPublish = [];
        const singleProductId = $('#bynder-single-product-id').val();

        if (singleProductId) {
            instancesToPublish.push(singleProductId);
        }

        const selectedProducts = $('.bynder-multi-product:checked').length;

        if (selectedProducts > 0) {
            $('.bynder-multi-product').each(function () {
                let instanceId = null;
                if ($(this).is(':checked')) {
                    instanceId = $(this).val();
                    instancesToPublish.push(instanceId);
                }
            });
        }

        if (instancesToPublish.length === 0) {
            toastr.options = {
                timeOut: 10000,
                extendedTimeOut: 0
            };

            toastr.error('Select at least one image');

            return;
        }

        if ($('#remember-bynder-settings').is(':checked')) {
            const settings = {
                bynder_dpi: $('#bynder-dpi').find(':selected').val(),
                bynder_image_type: $('#bynder-image-type').find(':selected').val(),
            }

            await storePublishPreferences(bynder.BYNDER_PUBLISH_SETTINGS, JSON.stringify(settings));
        } else {
            await storePublishPreferences(bynder.BYNDER_PUBLISH_SETTINGS, null);
        }

        const isCombinableFormat = imageType === bynder.BYNDER_IMAGE_TYPES.PDF || imageType === bynder.BYNDER_IMAGE_TYPES.PPTX;
        const shouldCombineIntoOneFile = isCombinableFormat && (publishAsOneFile || instancesToPublish.length === 1);

        const publishGroups = shouldCombineIntoOneFile
            ? [instancesToPublish]
            : instancesToPublish.map((instanceId) => [instanceId]);

        const totalInstances = publishGroups.length;
        let processedInstances = 0;

        if (uploadType === bynder.BYNDER_UPLOAD_TYPES.MULTIPLE && !publishInBackground) {
            progressBarContainer.removeClass('d-none');
            progressBar.css('width', '0%').attr('aria-valuenow', 0);
        }

        if (publishGroups.length > 0) {
            const backgroundFormData = new FormData();

            for (const instanceGroup of publishGroups) {
                const instanceId = instanceGroup[0];

                if (isBynderMeta) {
                    additionalProperties = store.getters['bynder/additionalPropertiesById'](instanceId);
                }

                if (additionalProperties) {
                    formData.set('additional_properties', JSON.stringify(additionalProperties));
                }

                let instance = previews.find(preview => preview.id === instanceId);

                let templateName = sanitizeTemplateName(instance);

                if (imageType === bynder.BYNDER_IMAGE_TYPES.PSD) {
                    includePsd = true;
                } else if (!publishInBackground && isCombinableFormat) {
                    let file = await buildFileForInstances(instanceGroup, templateName, imageType, dpi, generateExportFile);

                    formData.set('file', file);
                } else if (!publishInBackground) {
                    let file = await getImageFromInstance(instanceId, previews, dpi, imageType);

                    formData.set('file', file);
                }

                let gtin = instance.data?.gtin ?? null;
                let productName = instance.data?.product_name ?? null;
                let fileName = instance.data?.file_name ?? null;

                const updatedFileName = store.getters['bynder/filenameById'](instanceId);
                if (updatedFileName) {
                    fileName = updatedFileName;
                }

                if (gtin) {
                    formData.set('gtin', gtin);
                }

                if (productName) {
                    formData.set('product_name', productName);
                }

                if (fileName) {
                    formData.set('file_name', fileName);
                }

                formData.set('include_psd', includePsd);
                formData.set('instance_id', instanceId);
                formData.set('template_name', templateName);

                btnLoading('#bynder-publish', 'Publishing...');

                if (publishInBackground) {
                    let fileData = null;
                    if (isCombinableFormat) {
                        fileData = await buildFileForInstances(instanceGroup, templateName, imageType, dpi, generateExportFile);
                    } else if (!includePsd) {
                        fileData = await getImageFromInstance(instanceId, previews, dpi, imageType);
                    }

                    const instanceData = {
                        instance_id: instanceId,
                        platform_id: platformId,
                        layout_link: layoutLink,
                        image_type: imageType,
                        layout_id: layoutId,
                        dpi: dpi,
                        publish_in_background: publishInBackground,
                        include_psd: includePsd,
                        template_name: templateName,
                        gtin: gtin,
                        product_name: productName,
                        file_name: fileName,
                        customer_id: customerId
                    };

                    if (isBynderMeta) {
                        instanceData.is_bynder_meta = true;
                        instanceData.bynder_meta = bynderMeta;
                    }

                    if (additionalProperties) {
                        instanceData.additional_properties = additionalProperties;
                    }

                    backgroundFormData.append(`files_data[${instanceId}]`, JSON.stringify(instanceData));
                    backgroundFormData.append('layout_id', layoutId);
                    backgroundFormData.append('customer_id', customerId);

                    if (fileData) {
                        backgroundFormData.append(`files[${instanceId}]`, fileData);
                    }

                    continue;
                } else {
                    try {
                        const { data } = await axios.post('/bynder/file-upload', formData);

                        if (data?.status === 'error') {
                            $('.bynder-image-error[data-instance-id="' + instanceId + '"]').html(data.message);

                            return;
                        }

                        $('.bynder-image-response[data-instance-id="' + instanceId + '"]').html(data.message);

                        toastr.success('Successfully published to Bynder');

                        if (uploadType === bynder.BYNDER_UPLOAD_TYPES.MULTIPLE) {
                            processedInstances++;
                            const progressPercent = Math.round((processedInstances / totalInstances) * 100);
                            progressBar.css('width', `${progressPercent}%`).attr('aria-valuenow', progressPercent).html(`${progressPercent.toFixed(0)}%`);
                        }

                        isPublishSuccess = true;
                    } catch (error) {
                        isPublishSuccess = false;
                        console.error('Error publishing instance:', error);
                    }
                }
            }

            if (publishInBackground) {
                try {
                    const { data } = await axios.post('/bynder/background-files-upload', backgroundFormData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                    if (data?.status === 'error') {
                        isPublishSuccess = false;

                        toastr.error(data.message);
                        return;
                    }

                    isPublishSuccess = true;

                    toastr.success(data.message);
                } catch (error) {
                    isPublishSuccess = false;
                    console.error('Error publishing instance:', error);
                }
            }
        }
    } catch (error) {
        console.error('Failed to publish to Bynder:', error);
    } finally {
        if (isPublishSuccess) {
            $('#cancelPublish').removeClass('btn-secondary').addClass('btn-primary');
        }

        btnLoaded('#bynder-publish', 'Publish to Bynder', isPublishSuccess);
    }
}

export const publishToOptimizely = async () => {
    clearOptimizelyResponse();

    toastr.clear();

    let isPublishSuccess = false;

    try {
        const selectedSavedSetting = getSelectedOptimizelySavedSettings();

        if (!selectedSavedSetting) {
            toastr.error('Select saved settings');
            return;
        }

        const settings = selectedSavedSetting.settings;

        const layoutId = $("#template-group-preview").attr("data-layout-id");
        const customerId = $("#template-group-preview").attr("data-customer-id");
        const layoutLink = window.location.href;
        const platformId = $('#deployment_platform').find(':selected').val();

        let selectedGroups = [];
        const singleProductId = $('#optimizely-single-product-id').val();

        if (singleProductId) {
            selectedGroups.push([singleProductId]);
        }

        $('.optimizely-multi-product').each(function () {
            if ($(this).is(':checked')) {
                const instanceIds = JSON.parse($(this).attr('data-instance-ids') || 'null') || [$(this).val()];
                selectedGroups.push(instanceIds);
            }
        });

        if (selectedGroups.length === 0) {
            toastr.options = {
                timeOut: 10000,
                extendedTimeOut: 0
            };

            toastr.error('Select at least one image');

            return;
        }

        await storePublishPreferences(optimizely.OPTIMIZELY_PUBLISH_SETTINGS, JSON.stringify({
            optimizely_saved_settings_id: $('#optimizely-saved-settings').find(':selected').val(),
        }), true);

        const isOneFile = isSingleFile(settings);

        const publishGroups = isOneFile
            ? [selectedGroups.flat()]
            : selectedGroups;

        const items = publishGroups.map((instanceGroup) => {
            const instanceId = instanceGroup[0];
            const templateName = $(`.optimizely-instance-id[value="${instanceId}"]`).data('template-name') || '';

            const metaFieldsContainer = isOneFile
                ? $('#optimizely-bulk-meta-fields')
                : $(`.optimizely-meta-fields[data-instance-id="${instanceId}"]`);

            const assetTitle = metaFieldsContainer.find('.optimizely-meta-title-input').val();

            const manualFields = {};

            metaFieldsContainer.find('.optimizely-meta-field-input').each(function () {
                const value = JSON.parse($(this).val() || 'null');

                if (value === null || (Array.isArray(value) && value.length === 0)) {
                    return;
                }

                manualFields[$(this).data('field-id')] = value;
            });

            return {
                instance_ids: instanceGroup,
                template_name: sanitizeTemplateName({ template: { name: templateName } }),
                title: assetTitle ? assetTitle.trim() : null,
                manual_fields: manualFields,
            };
        });

        btnLoading('#optimizely-publish', 'Publishing...');

        const { data } = await axios.post('/optimizely/file-upload', {
            platform_id: platformId,
            layout_id: layoutId,
            customer_id: customerId,
            layout_link: layoutLink,
            settings,
            items,
        });

        if (data.status === 'queued') {
            toastr.success(data.message);
            isPublishSuccess = true;
        } else if (data.status === 'error') {
            toastr.error(data.message);
        } else {
            let successCount = 0;
            let errorCount = 0;

            if (isOneFile) {
                const result = data.results[0];
                const target = result.status === 'error'
                    ? $('#optimizely-bulk-image-error')
                    : $('#optimizely-bulk-image-response');

                target.html(result.message);

                if (target.length) {
                    target[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
                }

                if (result.status === 'error') {
                    errorCount++;
                } else {
                    successCount++;
                }
            } else {
                data.results.forEach((result) => {
                    const target = result.status === 'error'
                        ? $(`.optimizely-image-error[data-instance-id="${result.instance_id}"]`)
                        : $(`.optimizely-image-response[data-instance-id="${result.instance_id}"]`);

                    target.html(result.message);

                    if (result.status === 'error') {
                        errorCount++;
                    } else {
                        successCount++;
                    }
                });
            }

            if (errorCount === 0) {
                toastr.success('Successfully published to Optimizely');
                isPublishSuccess = true;
            } else if (successCount === 0) {
                toastr.error('Failed to publish to Optimizely');
            } else {
                toastr.warning(`Published ${successCount} of ${successCount + errorCount} items — see details below`);
                isPublishSuccess = true;
            }
        }
    } catch (error) {
        console.error('Failed to publish to Optimizely:', error);
    } finally {
        if (isPublishSuccess) {
            $('#cancelPublish').removeClass('btn-secondary').addClass('btn-primary');
        }

        btnLoaded('#optimizely-publish', 'Publish to Optimizely', isPublishSuccess);
    }
}

export const storePublishPreferences = async (key, value, for_company = false) => {
    const layoutId = $('#template-group-preview').attr('data-layout-id');

    const { data } = await axios.post('/grid-layout/store-publish-preferences', {
        layout_id: layoutId,
        key: key,
        value: value,
        for_company: for_company
    });

    if (data.status === 'error') {
        toastr.error(data.message);
        return;
    }

    return true;
}

export const getPublishPreferences = async (key, for_company = false) => {
    const layoutId = $('#template-group-preview').attr('data-layout-id');

    const { data } = await axios.get('/grid-layout/get-publish-preferences', {
        params: {
            key: key,
            layout_id: layoutId,
            for_company: for_company
        }
    });

    return data;
}

export const useAdditionalPropertyLocale = (settings) => {
    if (settings.additional_property_input) {
        $('#additional-property-input').val(settings.additional_property_input);
    }

    if (settings.additional_property_select) {
        const $select = $('#additional-property-select');

        $select.multipleSelect('setSelects', [settings.additional_property_select]);
        $select.multipleSelect('refresh');
    }
}

export const truncateImgName = (name, length = MAX_NAME_LENGHT) => {
    if (name.length > length) {
        const startLength = Math.ceil(length / 2) - 1;
        const endLength = Math.floor(length / 2) - 1;

        return name.slice(0, startLength) + '…' + name.slice(-endLength);
    } else {
        return name;
    }
}

export const clearAssetSelection = (assetId, assetType) => {
    $('.walmart-use-buttons[data-asset-id!="' + assetId + '"]').each(function () {
        if ($(this).data('asset-type') === assetType) {
            const mappedAssetId = $(this).data('asset-id');

            $('.drop-asset-selection-btn[data-asset-id="' + mappedAssetId + '"]').prop('disabled', true);
            $(this).text('Select image type').data('asset-type', '');
        }
    });
}

export const fillAddPropsSelect = (canvas) => {
    const properties = canvas.getObjects().filter(el =>
        el?.templateField?.type === FIELD_TYPE_TEXT ||
        el?.templateField?.type === FIELD_TYPE_MULTI_LINE_TEXT
    );

    const $select = $('#additional-property-select');

    $select.multipleSelect('destroy');

    $select.empty();

    if (properties.length) {
        $select.append(`<option value="">Select Field</option>`);
        properties.forEach(el => {
            const fieldtext = el?.text || '';
            const elementId = el?.templateField?.element_id;
            const fieldName = el?.templateField?.name;

            const isDate = isExcelDate(fieldtext) || isStandardDate(fieldtext);

            const badge = isDate ? `<span class="badge badge-success">Date</span>` : '';

            $select.append(`
                <option value="${elementId}">
                    ${fieldName} ${badge}
                </option>
            `);
        });
    } else {
        $select.append(`<option value="" selected disabled>No additional properties</option>`);
    }

    $select.multipleSelect({
        filter: true,
        placeholder: "Search field...",
        dropWidth: '100%',
        width: '100%'
    });
}

export const getFieldText = (previews, instanceId, elementId) => {
    const preview = previews.find(preview => preview.id === instanceId);

    const field = preview?.canvas.getObjects().find(el => el?.templateField?.element_id === elementId);

    const fieldText = field?.text || '';

    return fieldText;
}

export const isExcelDate = (value) => {
    return !isNaN(value) && value > 25569 && value < 60000;
};

export const isStandardDate = (value) => {
    const standardDateRegex = /^(\d{4}|\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4}|\d{1,2})$/;

    const isoFormatRegex = /^\d{8}$/;

    return standardDateRegex.test(value) || isoFormatRegex.test(value);
};

export const formatDateToISO = (value) => {
    if (isExcelDate(value)) {
        return excelToISODate(value);
    }

    if (/^\d{8}$/.test(value)) {
        const year = value.substring(0, 4);
        const month = value.substring(4, 6);
        const day = value.substring(6, 8);
        return `${year}-${month}-${day}`;
    }

    const match = value.match(/^(\d{4}|\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4}|\d{1,2})$/);

    if (!match) {
        return null;
    }

    const first = match[1];
    const second = match[2];
    const third = match[3];

    if (first.length === 4) {
        return `${first}-${second}-${third}`;
    } else {
        return `${third}-${first.padStart(2, '0')}-${second.padStart(2, '0')}`;
    }
};

export const excelToISODate = (excelDate) => {
    const date = new Date((excelDate - 25569) * 86400000);
    return date.toISOString().split('T')[0];
};

export const isBynderMetaAvailable = async () => {
    const { data } = await axios.get('/bynder-metadata/is-meta-available');

    return data;
}

export const useBynderPreferences = async () => {
    await loadBynderSavedSettings();

    try {
        const settings = await getPublishPreferences(bynder.BYNDER_PUBLISH_SETTINGS);

        if (settings) {
            $('#remember-bynder-settings').prop('checked', true);

            $('#bynder-dpi').val(settings.bynder_dpi).prop('selected', true).trigger('change');
            $('#bynder-image-type').val(settings.bynder_image_type).prop('selected', true).trigger('change');
        }
    } catch (error) {
        console.error('Error parsing saved Bynder settings', error);
    }
}

export const useOptimizelyPreferences = async () => {
    await loadOptimizelySavedSettings();

    try {
        const settings = await getPublishPreferences(optimizely.OPTIMIZELY_PUBLISH_SETTINGS, true);

        if (settings?.optimizely_saved_settings_id) {
            $('#optimizely-saved-settings').val(settings.optimizely_saved_settings_id).trigger('change');
        }
    } catch (error) {
        console.error('Error parsing saved Optimizely settings', error);
    }
}

const generateWalmartAssetTypeOptions = (includeDefault = false) => {
    const assetTypes = [
        { value: walmart.adUnits.MARQUEE_DESKTOP, label: 'Marquee Desktop' },
        { value: walmart.adUnits.MARQUEE_APP, label: 'Marquee App' },
        { value: walmart.adUnits.SKYLINE_DESKTOP, label: 'Skyline Desktop' },
        { value: walmart.adUnits.BRANDBOX_DESKTOP, label: 'Brandbox Desktop' },
        { value: walmart.adUnits.BRANDBOX_APP, label: 'Brandbox App' },
        { value: walmart.adUnits.GALLERY_DESKTOP, label: 'Gallery Desktop' },
        { value: walmart.adUnits.GALLERY_APP, label: 'Gallery App' },
        { value: walmart.adUnits.LOGO, label: 'Logo' },
        { value: walmart.adUnits.SKYLINE_DESKTOP_V2, label: 'Skyline Desktop V2' },
        { value: walmart.adUnits.SKYLINE_DESKTOP_V3, label: 'Skyline Desktop V3' },
        { value: walmart.adUnits.SKYLINE_APP_V3, label: 'Skyline App V3' },
        { value: walmart.adUnits.TILE_DESKTOP, label: 'Tile Desktop' },
        { value: walmart.adUnits.TILE_APP, label: 'Tile App' },
        { value: '200x260', label: 'Logo 200x260' },
        { value: '112x112', label: 'Logo 112x112' },
        { value: '220x100', label: 'Logo 220x100' },
        { value: '120x72', label: 'Logo 120x72' },
        { value: '154x92', label: 'Logo 154x92' },
        { value: '324x112', label: 'Logo 324x112' }
    ];

    let options = '';

    if (includeDefault) {
        options += '<option value="">Select asset type...</option>';
    }

    options += assetTypes.map(type =>
        `<option value="${type.value}">${type.label}</option>`
    ).join('\n            ');

    return options;
};

export const renderWalmartAssetTypeSelect = (instanceId, selectClass = 'walmart-creative-image-type') => {
    return `
    <label class="input-label">Asset type</label>
    <div class="walmart-asset-select-wrapper" style="position: relative; display: inline-block; width: 100%;">
        <select class="form-control form-control-sm ${selectClass}" data-instance-id="${instanceId}">
            ${generateWalmartAssetTypeOptions(true)}
        </select>
        <button type="button" class="walmart-asset-clear-btn" data-instance-id="${instanceId}"
                style="position: absolute; right: 25px; top: 50%; transform: translateY(-50%);
                       background: none; border: none; color: #999; font-size: 24px; cursor: pointer;
                       display: none; padding: 0; width: 16px; height: 16px; line-height: 16px;">
            ×
        </button>
    </div>
    `;
}

export const renderWalmartAssetTypeSelectFlex = (instanceId, selectClass = 'walmart-creative-image-type-flex') => {
    return `
    <label class="input-label">Asset type</label>
    <select class="form-control form-control-sm ${selectClass}" data-instance-id="${instanceId}">
        ${generateWalmartAssetTypeOptions(false)}
    </select>
    `;
}

export const toggleWalmartAssetTypeClearButton = ($select) => {
    const $wrapper = $select.closest('.walmart-asset-select-wrapper');
    const $clearBtn = $wrapper.find('.walmart-asset-clear-btn');

    if ($select.val() && $select.val() !== '') {
        $clearBtn.show();
    } else {
        $clearBtn.hide();
    }
};

export const clearWalmartAssetTypeSelection = (instanceId) => {
    const $select = $(`.walmart-creative-image-type[data-instance-id="${instanceId}"]`);
    const $wrapper = $select.closest('.walmart-asset-select-wrapper');
    const $clearBtn = $wrapper.find('.walmart-asset-clear-btn');

    $select.val('').trigger('change');
    $clearBtn.hide();

    $(`.walmart-creative-image-bulk[value="${instanceId}"]`).prop('checked', false).trigger('change');
};

export const detectAssetTypeFlex = (instanceId) => {
    const dimension = canvas_data[instanceId].dimension || {};

    const templateName = canvas_data[instanceId]?.templateInstance?.name;

    const hasTemplateDimensions = dimension.width !== undefined && dimension.height !== undefined;

    if (hasTemplateDimensions) {
        let key = `${dimension.width}x${dimension.height}`;

        if (key === '580x870') {
            if (templateName.includes('mWeb') || templateName.includes('App') || templateName.includes('Mobile')) {
                key = '580x870m';
            }
        }

        const creativeType = walmart.adUnitsDimensions[key];

        if (creativeType) {
            $(`.walmart-creative-image-type-flex[data-instance-id="${instanceId}"]`).val(creativeType);
        }
    }
}

export const appendAdditionalMetaproprties = async () => {
    await new Promise((resolve) => {
        setTimeout(resolve, 0);
    });

    const $containers = $('.bynder-multi-product-container').filter(function () {
        return $(this).closest('.d-none').length === 0;
    });

    const groupedVisible = $('#bynder-grouped-products').closest('.d-none').length === 0;

    const isOneFile = groupedVisible
        && isSingleFile(getSelectedBynderSavedSettings()?.settings);

    if (isOneFile) {
        $containers.find('.additional-metaproperties').addClass('d-none');
        $('#bynder-bulk-additional-metaproperties').removeClass('d-none')
            .find('.additional-metaproperties').removeClass('d-none');

        bynderEventBroker.fire(BYNDER_EVENTS.HANDLE_ADDITIONAL_PROPERTIES, {
            instanceId: bynder.BYNDER_ONE_FILE_INSTANCE_ID,
            previews: previews
        });

        return;
    }

    $('#bynder-bulk-additional-metaproperties').addClass('d-none');

    $containers.find('.additional-metaproperties').removeClass('d-none');

    $containers.each(function () {
        const instanceId = $(this).find('.bynder-instance-id').val();

        bynderEventBroker.fire(BYNDER_EVENTS.HANDLE_ADDITIONAL_PROPERTIES, {
            instanceId: instanceId,
            previews: previews
        });
    });
}

export const collectLocaleSettings = () => {
    return {
        additional_property_input: $('#additional-property-input').val(),
        additional_property_select: $('#additional-property-select').find(':selected').val(),
        property_locale: $('#property_locale').find(':selected').val(),
    };
}

export function initCharCounter(selector) {
    $(selector).each(function () {
        const $input = $(this)
        const maxLength = $input.data('maxlength') || 255

        // Create a counter-badge
        let $label = $input.siblings('label.creative-label')
        if (!$label.length) {
            $label = $input.closest('.col').find('label.creative-label')
        }
        $label.append(`<span class="badge badge-success ml-2">0/${maxLength}</span>`)

        const $counter = $label.find('.badge')
        const updateCounter = () => {
            const currentLength = $input.val().length
            $counter.text(`${currentLength}/${maxLength}`)
            $counter
                .toggleClass('badge-danger', currentLength > maxLength)
                .toggleClass('badge-success', currentLength <= maxLength)
        }

        // Init + bind
        updateCounter()
        $input.on('input', updateCounter)
    })
}

export const sanitizeName = (name) => {
    return name.replace(/[^a-zA-Z0-9]/g, '_');
}

export const walmartCheckTemplatesLength = () => {
    const templatesCount = store.getters['layout/allTemplatesCount'];

    if (previews.length < templatesCount) {
        $('#walmart-warning-container').removeClass('d-none');
    } else {
        $('#walmart-warning-container').addClass('d-none');
    }
}
